import { useState } from 'react'
import { useAnalytics } from '../../../../../analytics'
import { BulletBars, type BulletBarItem } from '../../charts'
import { CfoSegmentedControl } from '../../CfoPagePrimitives'
import { setPendingCfoGlDetailHandoff } from '../../cfoGlDetailHandoff'
import { CFO_EVENTS, CFO_SURFACES } from '../../cfoTelemetry'
import {
  LENS_LABEL,
  LENS_ORDER,
  firstPresentLens,
  glHandoffForDrill,
  movers,
  rankRows,
  selectDimension,
} from '../ceoCockpitModel'
import { BASIS_LABEL, formatCurrencyCompact, formatDeltaGrammar, formatShare } from '../ceoFormat'
import { isHiddenAvailability } from '../ceoCockpitTypes'
import type { CeoBasisKind, CeoCockpitResponse, CeoDimensionKind, CeoDimensionRow, CeoPeriodKind } from '../ceoCockpitTypes'
import { CeoNotTrackedState } from './CeoNotTrackedState'

/** The comparative (tick) value for a row under the selected basis. */
function rowComparative(row: CeoDimensionRow, period: CeoPeriodKind, basis: CeoBasisKind): number | null {
  const block = row[period]
  if (!block) return null
  if (basis === 'plan') return block.plan?.expected_to_date ?? null
  if (basis === 'forecast') return block.forecast?.expected_to_date ?? null
  if (basis === 'prior_year') return block.prior_year?.value ?? null
  return block.prior_period?.value ?? null
}

export function CeoDimensionPanel({
  payload,
  period,
  basis,
  lens,
  focus,
  onLensChange,
  onFocusChange,
  onNavSelect,
  onAsk,
}: {
  payload: CeoCockpitResponse
  period: CeoPeriodKind
  basis: CeoBasisKind
  lens: CeoDimensionKind | null
  /** Focused row key (`fg_ceo_focus`), if any. */
  focus?: string | null
  onLensChange: (lens: CeoDimensionKind) => void
  /** Sets/clears the focused row; when omitted, rows render as plain (non-button) rows. */
  onFocusChange?: (focus: string | null) => void
  onNavSelect: (navId: string) => void
  onAsk?: (prompt: string) => void
}) {
  const analytics = useAnalytics()
  const [showAll, setShowAll] = useState(false)
  // A dimension this business cannot have is dropped from the lens selector
  // entirely — offering "Location" to a single-office law firm invites a click
  // that can only lead to an empty panel.
  const dimensions = (Array.isArray(payload.dimensions) ? payload.dimensions : []).filter(
    (dimension) => !isHiddenAvailability(dimension.availability?.status),
  )
  const selectedRaw = selectDimension(payload, lens) || firstPresentLens(payload)
  const selected =
    selectedRaw && isHiddenAvailability(selectedRaw.availability?.status) ? dimensions[0] || null : selectedRaw
  const options = LENS_ORDER
    .map((kind) => dimensions.find((dimension) => dimension.kind === kind))
    .filter((dimension): dimension is NonNullable<typeof dimension> => Boolean(dimension))
    .map((dimension) => ({
      id: dimension.kind,
      label: LENS_LABEL[dimension.kind],
    }))
  const availability = selected?.availability
  const rows = rankRows(selected, period, showAll ? 25 : 8)
  const move = movers(selected)
  const focusedRow = focus ? rows.find((row) => row.key === focus) || null : null

  const openGl = () => {
    const drill = (focusedRow || selected?.rows[0])?.drill
    const handoff = glHandoffForDrill(drill, focusedRow ? `${selected?.label}: ${focusedRow.label}` : selected?.label) || {
      search: '',
      startDate: payload.calendar[period].current.start,
      endDate: payload.as_of_date,
      filters: selected?.gl_column
        ? [{ column: selected.gl_column, values: (focusedRow ? [focusedRow] : rows).map((row) => row.label) }]
        : [],
      label: selected?.label,
    }
    setPendingCfoGlDetailHandoff(handoff)
    analytics.track({
      event: CFO_EVENTS.ceoPanelOpen,
      surface: CFO_SURFACES.ceo,
      props: { panel: 'dimensions', lens: selected?.kind || null, focus: focusedRow?.key || null, target: 'gl_detail' },
    })
    onNavSelect('gl_detail')
  }

  const items: BulletBarItem[] = rows.map((row) => ({
    key: row.key,
    label: row.label,
    actual: row[period]?.actual ?? null,
    comparative: rowComparative(row, period, basis),
    // Prior-year ghost only when the tick is not already prior year.
    ghost: basis === 'prior_year' ? null : row[period]?.prior_year?.value ?? null,
    share: row.share_ytd,
    rank: row.rank,
    deltaRank: row.delta_rank,
    secondary: row.gross_margin_pct_ytd,
    secondaryLabel: 'GM',
  }))

  const totalsActual = selected?.totals?.ytd?.actual
  const uncoded = availability && availability.status === 'partial' && Number.isFinite(availability.coverage_pct)
    ? Math.max(0, Math.round((1 - availability.coverage_pct) * 100))
    : null

  return (
    <section className="ceo-section rise" id="ceo-dimensions" data-testid="ceo-dimensions">
      <div className="ceo-dim-head">
        <p className="ceo-kicker">Where it&apos;s coming from</p>
        {options.length ? (
          <div className="ceo-lens-scroll">
            <CfoSegmentedControl
              label="Operating lens"
              value={selected?.kind || options[0]?.id || 'location'}
              onChange={(value) => onLensChange(value as CeoDimensionKind)}
              options={options}
            />
          </div>
        ) : null}
      </div>
      {availability?.status === 'not_tracked' || !selected ? (
        <CeoNotTrackedState connectTarget={availability?.connect_target} onAsk={onAsk} />
      ) : (
        <div className="ceo-dim-layout">
          <div className="ceo-dim-main">
            {uncoded != null ? (
              <p className="ceo-coverage">
                Covers {Math.round(availability!.coverage_pct * 100)}% · {uncoded}% uncoded → Fix coding
              </p>
            ) : null}
            {items.length ? (
              <BulletBars
                ariaLabel={`${selected.label} contribution, ${period.toUpperCase()} actual vs ${BASIS_LABEL[basis]}`}
                comparativeLabel={BASIS_LABEL[basis]}
                ghostLabel="prior year"
                items={items}
                selectedKey={focus ?? null}
                onSelect={
                  onFocusChange
                    ? (item) => onFocusChange(focus === item.key ? null : item.key)
                    : undefined
                }
              />
            ) : (
              <p className="ceo-muted">Not prepared — no {selected.label.toLowerCase()} rows for this period yet.</p>
            )}
            {selected.rows.length > 8 ? (
              <button type="button" className="ceo-text-btn" onClick={() => setShowAll((value) => !value)}>
                {showAll ? 'Show top 8' : `Show all ${selected.rows.length}`}
              </button>
            ) : null}
          </div>
          <aside className="ceo-movers">
            <p className="ceo-kicker">Movers · vs prior period</p>
            <div className="ceo-movers-group">
              <strong aria-label="Up">↑</strong>
              {move.up.length ? move.up.map((item) => (
                <p key={item.key}>
                  <span>{item.label}</span>
                  <span>{formatDeltaGrammar({ deltaAbs: item.delta_abs, deltaPct: item.delta_pct, basis: 'prior_period', compact: true }).replace(/ vs prior period$/, '')}</span>
                </p>
              )) : <p className="ceo-muted">None</p>}
            </div>
            <div className="ceo-movers-group">
              <strong aria-label="Down">↓</strong>
              {move.down.length ? move.down.map((item) => (
                <p key={item.key}>
                  <span>{item.label}</span>
                  <span>{formatDeltaGrammar({ deltaAbs: item.delta_abs, deltaPct: item.delta_pct, basis: 'prior_period', compact: true }).replace(/ vs prior period$/, '')}</span>
                </p>
              )) : <p className="ceo-muted">None</p>}
            </div>
            <button type="button" className="ceo-text-btn" onClick={openGl}>
              {focusedRow ? `Open ${focusedRow.label} in GL detail →` : 'Open in GL detail →'}
            </button>
            {totalsActual != null && Number.isFinite(totalsActual) && totalsActual !== 0 ? (
              <p className="ceo-muted">
                {selected.totals?.share_ytd != null ? `${formatShare(selected.totals.share_ytd)} of ` : ''}
                {formatCurrencyCompact(totalsActual)} YTD
              </p>
            ) : null}
          </aside>
        </div>
      )}
    </section>
  )
}
